/**
 * Riders — Workload distribution. Stage 7.
 * Sources `/api/bi/riders/distribution/`.
 */
import { useMemo, useState } from 'react';
import { ArrowLeft } from 'lucide-react';
import { Link } from 'react-router-dom';
import { type ColumnDef } from '@tanstack/react-table';
import { PageHeader } from '@/components/layout/PageHeader';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { Card, CardBody, CardHeader, CardTitle } from '@/components/ui/Card';
import { ErrorState } from '@/components/ui/ErrorState';
import { KPICard } from '@/components/charts/KPICard';
import { DataView } from '@/components/data/DataView';
import { useRidersDistribution, type RidersDistRow, type PercentileBundle } from '@/api/bi';
import { rangeISO, useFilters } from '@/state/filters';
import { fmt } from '@/lib/format';
import { cn } from '@/lib/cn';
import { openRiderOrders } from '@/state/riderOrdersDialog';

export function RidersDistributionPage() {
  const range = useFilters((s) => s.range);
  const storeIds = useFilters((s) => s.storeIds);
  const timeRangeMode = useFilters((s) => s.timeRangeMode);
  const { start: startISO, end: endISO } = useMemo(
    () => rangeISO(range, timeRangeMode),
    [range, timeRangeMode],
  );

  const query = useRidersDistribution({ start: startISO, end: endISO, storeIds });
  const data = query.data;
  const totals = data?.totals;
  const hist = data?.histogram ?? [];
  const histMax = Math.max(1, ...hist.map((h) => h.count));

  const [search, setSearch] = useState('');
  const filteredRiders = useMemo(() => {
    const rows = data?.top_riders ?? [];
    const s = search.trim().toLowerCase();
    if (!s) return rows;
    return rows.filter((r) => r.rider_name.toLowerCase().includes(s));
  }, [data?.top_riders, search]);

  const columns = useMemo<ColumnDef<RidersDistRow, unknown>[]>(
    () => [
      {
        id: 'rider',
        header: 'Rider',
        accessorKey: 'rider_name',
        cell: ({ row }) => (
          <div className="flex items-center gap-2">
            <span className="grid h-6 w-6 place-items-center rounded-full bg-primary/10 text-[10px] font-semibold text-primary">
              {row.original.rider_name.slice(0, 2).toUpperCase()}
            </span>
            <span className="font-medium text-foreground">{row.original.rider_name}</span>
          </div>
        ),
      },
      {
        id: 'deliveries',
        header: 'Deliveries',
        accessorKey: 'deliveries',
        cell: ({ getValue }) => <span className="kpi-num">{fmt.number(getValue<number>())}</span>,
      },
      {
        id: 'gmv',
        header: 'GMV',
        accessorKey: 'gmv',
        cell: ({ getValue }) => <span className="kpi-num">{fmt.npr(getValue<number>())}</span>,
      },
    ],
    [],
  );

  return (
    <>
      <PageHeader
        eyebrow="Strategic"
        title="Rider workload"
        description="How evenly we're spreading the load. Percentiles and a histogram make over- and under-utilised riders obvious."
        actions={
          <Button asChild variant="outline" size="sm">
            <Link to="/riders">
              <ArrowLeft className="h-3.5 w-3.5" /> Back
            </Link>
          </Button>
        }
      />

      <div className="mt-4 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <KPICard label="Active riders" value={fmt.number(totals?.riders ?? 0)} loading={query.isLoading} />
        <KPICard label="Deliveries" value={fmt.number(totals?.deliveries ?? 0)} loading={query.isLoading} />
        <KPICard
          label="Avg per rider"
          value={(totals?.avg_deliveries ?? 0).toFixed(1)}
          loading={query.isLoading}
        />
        <KPICard label="GMV handled" value={fmt.npr(totals?.gmv ?? 0)} loading={query.isLoading} />
      </div>

      <div className="mt-4 grid gap-4 lg:grid-cols-3">
        <Card>
          <CardHeader>
            <CardTitle>Deliveries percentiles</CardTitle>
          </CardHeader>
          <CardBody>
            <PercentileTable bundle={data?.deliveries_dist} format={(v) => fmt.number(v ?? 0)} />
          </CardBody>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>GMV percentiles</CardTitle>
          </CardHeader>
          <CardBody>
            <PercentileTable bundle={data?.gmv_dist} format={(v) => fmt.npr(v ?? 0)} />
          </CardBody>
        </Card>

        <Card className="lg:col-span-1">
          <CardHeader>
            <CardTitle>Histogram</CardTitle>
            <Badge tone="neutral">deliveries / rider</Badge>
          </CardHeader>
          <CardBody>
            {query.isError ? (
              <ErrorState description="Couldn't load histogram." onRetry={() => void query.refetch()} />
            ) : query.isLoading ? (
              <div className="h-44 animate-pulse rounded-md bg-muted/40" />
            ) : hist.length === 0 ? (
              <p className="text-sm text-muted-foreground">No riders in range.</p>
            ) : (
              <div className="space-y-1.5">
                {hist.map((b) => (
                  <div key={b.bucket} className="flex items-center gap-2">
                    <span className="w-12 font-mono text-[11px] text-muted-foreground">{b.bucket}</span>
                    <div className="flex-1">
                      <div
                        className={cn(
                          'h-3 rounded-full bg-primary/70 transition-[width]',
                          b.count === 0 && 'bg-muted/40',
                        )}
                        style={{ width: `${(b.count / histMax) * 100}%`, minWidth: b.count > 0 ? 6 : 0 }}
                      />
                    </div>
                    <span className="w-8 text-right text-[11px] tabular-nums text-muted-foreground">
                      {b.count}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </CardBody>
        </Card>
      </div>

      <div className="mt-4">
        {query.isError ? (
          <ErrorState description="Couldn't load riders." onRetry={() => void query.refetch()} />
        ) : (
          <DataView<RidersDistRow>
            title="Top riders"
            data={filteredRiders}
            columns={columns}
            isLoading={query.isLoading}
            search={{ value: search, onChange: setSearch, placeholder: 'Search rider…' }}
            emptyTitle="No deliveries in this window"
            onRowClick={(r) =>
              openRiderOrders({
                riderId: r.rider_id,
                riderName: r.rider_name,
              })
            }
          />
        )}
      </div>
    </>
  );
}

function PercentileTable({
  bundle,
  format,
}: {
  bundle: PercentileBundle | undefined;
  format: (v: number | null) => string;
}) {
  const rows: { label: string; value: number | null; tone?: 'danger' }[] = [
    { label: 'P10', value: bundle?.p10 ?? null },
    { label: 'P50 (median)', value: bundle?.p50 ?? null },
    { label: 'P90', value: bundle?.p90 ?? null },
    { label: 'P95', value: bundle?.p95 ?? null },
    { label: 'P99', value: bundle?.p99 ?? null, tone: 'danger' },
  ];
  return (
    <div className="space-y-1.5">
      {rows.map((r) => (
        <div key={r.label} className="flex items-baseline justify-between text-sm">
          <span className="font-mono text-[11px] uppercase tracking-wider text-muted-foreground">
            {r.label}
          </span>
          <span className={cn('kpi-num', r.tone === 'danger' && 'text-danger')}>{format(r.value)}</span>
        </div>
      ))}
    </div>
  );
}
